// EmployeeEditDialog.js
// Dialog for editing existing employees

import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormHelperText,
  InputAdornment,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import MobileDialog from './MobileDialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { updateEmployee, getDepartments, getEmployeeTypes } from '../../services/employeeService';

const formatDateLocal = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr);
};

const EmployeeEditDialog = ({ open, onClose, employee }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    FirstName: '',
    LastName: '',
    IDNumber: '',
    EmployeeNumber: '',
    PhoneNumber: '',
    Email: '',
    Address: '',
    DepartmentIDs: [],
    EmployeeTypeID: '',
    HireDate: null,
    TerminationDate: null,
    Status: 'active',
    WageType: 'monthly',
    WageAmount: '',
    Notes: '',
  });

  // Fetch departments and employee types
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: getDepartments,
    enabled: open,
  });

  const { data: employeeTypes = [] } = useQuery({
    queryKey: ['employeeTypes'],
    queryFn: getEmployeeTypes,
    enabled: open,
  });

  // Initialize form when dialog opens or employee changes
  useEffect(() => {
    if (open && employee) {
      setActiveTab(0);
      setFormData({
        FirstName: employee.FirstName || '',
        LastName: employee.LastName || '',
        IDNumber: employee.IDNumber || '',
        EmployeeNumber: employee.EmployeeNumber || '',
        PhoneNumber: employee.PhoneNumber || '',
        Email: employee.Email || '',
        Address: employee.Address || '',
        DepartmentIDs: employee.Departments?.map((d) => d.DepartmentID) || [],
        EmployeeTypeID: employee.EmployeeType?.TypeID || '',
        HireDate: parseDate(employee.HireDate),
        TerminationDate: parseDate(employee.TerminationDate),
        Status: employee.Status || 'active',
        WageType: employee.WageType || 'monthly',
        WageAmount: employee.WageAmount ? String(parseFloat(employee.WageAmount)) : '',
        Notes: employee.Notes || '',
      });
      setError('');
    }
  }, [open, employee]);

  // Scroll to error
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const updateMutation = useMutation({
    mutationFn: updateEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      onClose();
    },
    onError: (err) => {
      setError(err?.response?.data?.error || 'שגיאה בעדכון עובד');
      setIsSubmitting(false);
    },
  });

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDateChange = (field) => (date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!formData.FirstName.trim()) {
      setError('שם פרטי נדרש');
      return;
    }
    if (!formData.LastName.trim()) {
      setError('שם משפחה נדרש');
      return;
    }
    if (!formData.HireDate) {
      setError('תאריך תחילת עבודה נדרש');
      return;
    }
    if (!formData.WageAmount || parseFloat(formData.WageAmount) <= 0) {
      setError('סכום שכר נדרש');
      return;
    }

    setIsSubmitting(true);

    const data = {
      EmployeeID: employee.EmployeeID,
      FirstName: formData.FirstName.trim(),
      LastName: formData.LastName.trim(),
      IDNumber: formData.IDNumber.trim() || null,
      EmployeeNumber: formData.EmployeeNumber.trim() || null,
      PhoneNumber: formData.PhoneNumber.trim() || null,
      Email: formData.Email.trim() || null,
      Address: formData.Address.trim() || null,
      DepartmentIDs: formData.DepartmentIDs,
      EmployeeTypeID: formData.EmployeeTypeID || null,
      HireDate: formatDateLocal(formData.HireDate),
      TerminationDate: formatDateLocal(formData.TerminationDate),
      Status: formData.Status,
      WageType: formData.WageType,
      WageAmount: parseFloat(formData.WageAmount),
      Notes: formData.Notes.trim() || null,
    };

    updateMutation.mutate(data);
  };

  const renderPersonalInfo = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="שם פרטי"
          value={formData.FirstName}
          onChange={handleChange('FirstName')}
          fullWidth
          required
        />
        <TextField
          label="שם משפחה"
          value={formData.LastName}
          onChange={handleChange('LastName')}
          fullWidth
          required
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="תעודת זהות"
          value={formData.IDNumber}
          onChange={handleChange('IDNumber')}
          fullWidth
        />
        <TextField
          label="מספר עובד"
          value={formData.EmployeeNumber}
          onChange={handleChange('EmployeeNumber')}
          fullWidth
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="טלפון"
          value={formData.PhoneNumber}
          onChange={handleChange('PhoneNumber')}
          fullWidth
        />
        <TextField
          label="אימייל"
          type="email"
          value={formData.Email}
          onChange={handleChange('Email')}
          fullWidth
        />
      </Box>
      <TextField
        label="כתובת"
        value={formData.Address}
        onChange={handleChange('Address')}
        fullWidth
        multiline
        rows={2}
      />
    </Box>
  );

  const renderEmploymentInfo = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl fullWidth>
        <InputLabel>מחלקות</InputLabel>
        <Select
          multiple
          value={formData.DepartmentIDs}
          onChange={handleChange('DepartmentIDs')}
          input={<OutlinedInput label="מחלקות" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((id) => {
                const dept = departments.find((d) => d.DepartmentID === id);
                return <Chip key={id} label={dept?.Name || id} size="small" />;
              })}
            </Box>
          )}
        >
          {departments.map((dept) => (
            <MenuItem key={dept.DepartmentID} value={dept.DepartmentID}>
              {dept.Name}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>ניתן לבחור מספר מחלקות</FormHelperText>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>סוג עובד</InputLabel>
        <Select
          value={formData.EmployeeTypeID}
          onChange={handleChange('EmployeeTypeID')}
          label="סוג עובד"
        >
          <MenuItem value="">
            <em>ללא</em>
          </MenuItem>
          {employeeTypes.map((type) => (
            <MenuItem key={type.TypeID} value={type.TypeID}>
              {type.Name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
        <DatePicker
          label="תאריך תחילת עבודה"
          value={formData.HireDate}
          onChange={handleDateChange('HireDate')}
          format="dd/MM/yyyy"
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              sx: { '& .MuiInputBase-root': { direction: 'ltr' } },
            },
          }}
        />

        <DatePicker
          label="תאריך סיום עבודה"
          value={formData.TerminationDate}
          onChange={handleDateChange('TerminationDate')}
          format="dd/MM/yyyy"
          slotProps={{
            textField: {
              fullWidth: true,
              sx: { '& .MuiInputBase-root': { direction: 'ltr' } },
            },
          }}
        />
      </LocalizationProvider>

      <FormControl fullWidth>
        <InputLabel>סטטוס</InputLabel>
        <Select
          value={formData.Status}
          onChange={handleChange('Status')}
          label="סטטוס"
        >
          <MenuItem value="active">פעיל</MenuItem>
          <MenuItem value="inactive">לא פעיל</MenuItem>
          <MenuItem value="terminated">סיים עבודה</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  const renderWageInfo = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl fullWidth>
        <InputLabel>סוג שכר</InputLabel>
        <Select
          value={formData.WageType}
          onChange={handleChange('WageType')}
          label="סוג שכר"
        >
          <MenuItem value="monthly">חודשי</MenuItem>
          <MenuItem value="hourly">שעתי</MenuItem>
          <MenuItem value="daily">יומי</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label={formData.WageType === 'hourly' ? 'שכר לשעה' : formData.WageType === 'daily' ? 'שכר ליום' : 'שכר חודשי'}
        type="number"
        value={formData.WageAmount}
        onChange={handleChange('WageAmount')}
        fullWidth
        required
        InputProps={{
          startAdornment: <InputAdornment position="start">₪</InputAdornment>,
        }}
      />

      <TextField
        label="הערות"
        value={formData.Notes}
        onChange={handleChange('Notes')}
        fullWidth
        multiline
        rows={3}
      />
    </Box>
  );

  if (!employee) return null;

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        עריכת עובד - {employee.FirstName} {employee.LastName}
      </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert ref={errorRef} severity="error">{error}</Alert>
            )}

            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
            >
              <Tab label="פרטים אישיים" />
              <Tab label="העסקה" />
              <Tab label="שכר" />
            </Tabs>

            <Box sx={{ mt: 2, minHeight: 300 }}>
              {activeTab === 0 && renderPersonalInfo()}
              {activeTab === 1 && renderEmploymentInfo()}
              {activeTab === 2 && renderWageInfo()}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'שמור שינויים'}
          </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default EmployeeEditDialog;
